import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Users, 
  CheckCircle, 
  XCircle,
  Clock,
  CurrencyDollar,
  ArrowLeft,
  Eye,
  Spinner,
  Warning,
  Medal,
  MapPin,
  House,
  Crown,
  PencilSimple,
  X
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tier configuration
const TIER_CONFIG = {
  base: { label: 'Base', color: 'text-zinc-400', rate: 0.50 },
  silver: { label: 'Silver', color: 'text-gray-300', rate: 0.60 },
  gold: { label: 'Gold', color: 'text-yellow-400', rate: 0.70 },
  platinum: { label: 'Platinum', color: 'text-blue-400', rate: 0.85 },
  founder: { label: 'Founder', color: 'text-[#D4AF37]', rate: 1.00 },
};

export default function AdminAffiliatesPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('applications');
  const [applications, setApplications] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [affiliateDetails, setAffiliateDetails] = useState(null);
  const [showTierOverride, setShowTierOverride] = useState(false);
  const [tierOverrideData, setTierOverrideData] = useState({
    tier: '',
    is_founder: false,
    reset_window: false
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      // Load stats
      const statsRes = await fetch(`${API_URL}/api/admin/affiliates/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      // Load applications
      const appsRes = await fetch(`${API_URL}/api/admin/affiliates/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (appsRes.ok) {
        setApplications(await appsRes.json());
      }

      // Load affiliates
      const affsRes = await fetch(`${API_URL}/api/admin/affiliates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (affsRes.ok) {
        setAffiliates(await affsRes.json());
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationAction = async (applicationId, action, adminNotes = '') => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/affiliates/applications/${applicationId}/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ action, admin_notes: adminNotes })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Action failed');
      }

      toast.success(data.message);
      setSelectedApplication(null);
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const loadAffiliateDetails = async (affiliateId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/affiliates/${affiliateId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAffiliateDetails(data);
        setSelectedAffiliate(affiliateId);
      }
    } catch (error) {
      toast.error('Failed to load affiliate details');
    }
  };

  const handleTierOverride = async () => {
    if (!selectedAffiliate) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/affiliates/${selectedAffiliate}/tier-override`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(tierOverrideData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update tier');
      }

      toast.success('Affiliate tier updated successfully');
      setShowTierOverride(false);
      setTierOverrideData({ tier: '', is_founder: false, reset_window: false });
      loadData();
      loadAffiliateDetails(selectedAffiliate);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAffiliateAction = async (affiliateId, action) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/affiliates/${affiliateId}/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ action })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Action failed');
      }

      toast.success(data.message);
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Spinner size={48} className="text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const pendingApplications = applications.filter(a => a.status === 'pending');

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-serif text-white">Affiliate Management</h1>
            <p className="text-zinc-400">Manage affiliate applications and accounts</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Clock size={20} className="text-yellow-500" />
              <span className="text-zinc-400 text-sm">Pending Applications</span>
            </div>
            <div className="text-2xl font-semibold text-white">{stats?.pending_applications || 0}</div>
          </div>
          
          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users size={20} className="text-[#D4AF37]" />
              <span className="text-zinc-400 text-sm">Active Affiliates</span>
            </div>
            <div className="text-2xl font-semibold text-white">{stats?.active_affiliates || 0}</div>
          </div>
          
          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users size={20} className="text-blue-500" />
              <span className="text-zinc-400 text-sm">Referred Hosts</span>
            </div>
            <div className="text-2xl font-semibold text-white">{stats?.total_referred_hosts || 0}</div>
          </div>
          
          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-2">
              <CurrencyDollar size={20} className="text-green-500" />
              <span className="text-zinc-400 text-sm">Total Commissions</span>
            </div>
            <div className="text-2xl font-semibold text-white">${stats?.earnings?.total?.toFixed(2) || '0.00'}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-white/10 mb-6">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('applications')}
              className={`pb-3 px-1 text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'applications'
                  ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Applications
              {pendingApplications.length > 0 && (
                <span className="bg-yellow-500 text-black text-xs px-2 py-0.5 rounded-full">
                  {pendingApplications.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('affiliates')}
              className={`pb-3 px-1 text-sm font-medium transition-colors ${
                activeTab === 'affiliates'
                  ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Affiliates
            </button>
          </div>
        </div>

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="bg-[#121212] border border-white/10">
            {applications.length === 0 ? (
              <div className="p-12 text-center">
                <Users size={48} className="text-zinc-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No applications yet</h3>
                <p className="text-zinc-400">Affiliate applications will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Applicant</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Type</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Est. Hosts</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Date</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Status</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app) => (
                      <tr key={app.id} className="border-b border-white/5">
                        <td className="py-4 px-6">
                          <div>
                            <p className="text-white font-medium">{app.full_name}</p>
                            <p className="text-zinc-500 text-sm">{app.email}</p>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-zinc-400 capitalize">
                          {app.affiliate_type?.replace(/_/g, ' ')}
                        </td>
                        <td className="py-4 px-6 text-white">{app.estimated_hosts}</td>
                        <td className="py-4 px-6 text-zinc-400">
                          {new Date(app.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-6">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            app.status === 'approved' 
                              ? 'bg-green-500/10 text-green-500'
                              : app.status === 'rejected'
                              ? 'bg-red-500/10 text-red-500'
                              : 'bg-yellow-500/10 text-yellow-500'
                          }`}>
                            {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-white/20 text-white hover:bg-white/5"
                              onClick={() => setSelectedApplication(app)}
                            >
                              <Eye size={14} className="mr-1" />
                              View
                            </Button>
                            {app.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => handleApplicationAction(app.id, 'approve')}
                                  disabled={actionLoading}
                                >
                                  <CheckCircle size={14} className="mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleApplicationAction(app.id, 'reject')}
                                  disabled={actionLoading}
                                >
                                  <XCircle size={14} className="mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Affiliates Tab */}
        {activeTab === 'affiliates' && (
          <div className="bg-[#121212] border border-white/10">
            {affiliates.length === 0 ? (
              <div className="p-12 text-center">
                <Users size={48} className="text-zinc-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No affiliates yet</h3>
                <p className="text-zinc-400">Approved affiliates will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Affiliate</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Tier</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Referral Code</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Referred Hosts</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Earnings</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Status</th>
                      <th className="text-left py-4 px-6 text-zinc-400 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affiliates.map((aff) => (
                      <tr key={aff.id} className="border-b border-white/5">
                        <td className="py-4 px-6">
                          <div>
                            <p className="text-white font-medium">{aff.full_name}</p>
                            <p className="text-zinc-500 text-sm">{aff.email}</p>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            {aff.is_founder && <Crown size={16} className="text-[#D4AF37]" />}
                            <span className={`${TIER_CONFIG[aff.tier || 'base']?.color || 'text-zinc-400'} font-medium`}>
                              {TIER_CONFIG[aff.tier || 'base']?.label || 'Base'}
                            </span>
                            <span className="text-zinc-500 text-xs">
                              (${aff.commission_rate?.toFixed(2) || '0.50'})
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span className="font-mono text-[#D4AF37]">{aff.referral_code}</span>
                        </td>
                        <td className="py-4 px-6 text-white">{aff.total_referred_hosts || 0}</td>
                        <td className="py-4 px-6 text-green-500 font-medium">
                          ${aff.lifetime_earnings?.toFixed(2) || '0.00'}
                        </td>
                        <td className="py-4 px-6">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            aff.status === 'approved' 
                              ? 'bg-green-500/10 text-green-500'
                              : 'bg-red-500/10 text-red-500'
                          }`}>
                            {aff.status === 'approved' ? 'Active' : 'Suspended'}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-white/20 text-white hover:bg-white/5"
                              onClick={() => loadAffiliateDetails(aff.id)}
                            >
                              <Eye size={14} className="mr-1" />
                              Details
                            </Button>
                            {aff.status === 'approved' ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                                onClick={() => handleAffiliateAction(aff.id, 'suspend')}
                                disabled={actionLoading}
                              >
                                <Warning size={14} className="mr-1" />
                                Suspend
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleAffiliateAction(aff.id, 'unsuspend')}
                                disabled={actionLoading}
                              >
                                <CheckCircle size={14} className="mr-1" />
                                Reactivate
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Application Detail Modal */}
        {selectedApplication && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
            <div className="bg-[#121212] border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-white/10">
                <h2 className="text-xl font-semibold text-white">Application Details</h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-zinc-400 text-sm">Full Name</p>
                    <p className="text-white">{selectedApplication.full_name}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Email</p>
                    <p className="text-white">{selectedApplication.email}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Phone</p>
                    <p className="text-white">{selectedApplication.phone}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Company</p>
                    <p className="text-white">{selectedApplication.company_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Type</p>
                    <p className="text-white capitalize">{selectedApplication.affiliate_type?.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Estimated Hosts</p>
                    <p className="text-white">{selectedApplication.estimated_hosts}</p>
                  </div>
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Website/Social</p>
                  <p className="text-white">{selectedApplication.website_or_social || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Audience Description</p>
                  <p className="text-white">{selectedApplication.audience_description}</p>
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Why They Want to Join</p>
                  <p className="text-white">{selectedApplication.why_join}</p>
                </div>
              </div>
              <div className="p-6 border-t border-white/10 flex justify-end gap-3">
                <Button
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5"
                  onClick={() => setSelectedApplication(null)}
                >
                  Close
                </Button>
                {selectedApplication.status === 'pending' && (
                  <>
                    <Button
                      variant="destructive"
                      onClick={() => handleApplicationAction(selectedApplication.id, 'reject')}
                      disabled={actionLoading}
                    >
                      Reject
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => handleApplicationAction(selectedApplication.id, 'approve')}
                      disabled={actionLoading}
                    >
                      Approve
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Affiliate Details Modal */}
        {affiliateDetails && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#121212] border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-white/10 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Affiliate Details</h3>
                <button 
                  onClick={() => { setAffiliateDetails(null); setSelectedAffiliate(null); }}
                  className="text-zinc-400 hover:text-white"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-zinc-400 text-sm">Name</p>
                    <p className="text-white font-medium">{affiliateDetails.full_name}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Email</p>
                    <p className="text-white">{affiliateDetails.email}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Referral Code</p>
                    <p className="font-mono text-[#D4AF37]">{affiliateDetails.referral_code}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Joined</p>
                    <p className="text-white">{new Date(affiliateDetails.created_at).toLocaleDateString()}</p>
                  </div>
                </div>

                {/* Tier Info */}
                <div className="bg-[#0a0a0a] border border-white/5 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {affiliateDetails.is_founder && <Crown size={24} className="text-[#D4AF37]" />}
                      <div>
                        <p className="text-zinc-400 text-sm">Current Tier</p>
                        <p className={`text-xl font-semibold ${TIER_CONFIG[affiliateDetails.tier || 'base']?.color}`}>
                          {TIER_CONFIG[affiliateDetails.tier || 'base']?.label}
                          {affiliateDetails.is_founder && ' (Lifetime)'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-zinc-400 text-sm">Commission Rate</p>
                      <p className="text-2xl font-serif text-[#D4AF37]">${affiliateDetails.commission_rate?.toFixed(2)}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-zinc-400">Host Count</p>
                      <p className="text-white font-medium">{affiliateDetails.host_count}</p>
                    </div>
                    <div>
                      <p className="text-zinc-400">Window Status</p>
                      <p className={`font-medium ${affiliateDetails.window_status === 'active' ? 'text-green-500' : 'text-red-500'}`}>
                        {affiliateDetails.window_status === 'active' ? 'Active' : 'Expired'}
                        {affiliateDetails.window_remaining_days && ` (${affiliateDetails.window_remaining_days} days left)`}
                      </p>
                    </div>
                    <div>
                      <p className="text-zinc-400">Founder Eligible</p>
                      <p className={`font-medium ${affiliateDetails.founder_eligible ? 'text-green-500' : 'text-zinc-500'}`}>
                        {affiliateDetails.founder_eligible ? 'Yes - Ready to upgrade!' : 'No'}
                      </p>
                    </div>
                    <div>
                      <p className="text-zinc-400">Total Earnings</p>
                      <p className="text-green-500 font-medium">${affiliateDetails.earnings_summary?.total?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                </div>

                {/* Host Breakdown by State */}
                {Object.keys(affiliateDetails.state_breakdown || {}).length > 0 && (
                  <div>
                    <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                      <MapPin size={18} className="text-blue-400" />
                      Hosts by State
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(affiliateDetails.state_breakdown).map(([state, count]) => (
                        <div 
                          key={state} 
                          className={`px-3 py-1 rounded text-sm ${count >= 50 ? 'bg-blue-500/20 border border-blue-500/30 text-blue-300' : 'bg-white/5 text-zinc-400'}`}
                        >
                          {state}: {count} {count >= 50 && <Medal size={14} className="inline ml-1" />}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Badges */}
                {(affiliateDetails.state_badges?.length > 0 || affiliateDetails.zip_badges?.length > 0) && (
                  <div>
                    <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                      <Medal size={18} className="text-[#D4AF37]" />
                      Earned Badges
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {affiliateDetails.state_badges?.map(state => (
                        <div key={state} className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 px-3 py-1 rounded-full text-sm">
                          <MapPin size={14} className="text-blue-400" />
                          <span className="text-blue-300">{state} State Builder</span>
                        </div>
                      ))}
                      {affiliateDetails.zip_badges?.map(zip => (
                        <div key={zip} className="flex items-center gap-2 bg-green-500/10 border border-green-500/30 px-3 py-1 rounded-full text-sm">
                          <House size={14} className="text-green-400" />
                          <span className="text-green-300">Local Ambassador ({zip})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Admin Actions */}
                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <PencilSimple size={18} className="text-[#D4AF37]" />
                    Admin Actions
                  </h4>
                  <Button
                    variant="outline"
                    className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                    onClick={() => {
                      setTierOverrideData({
                        tier: affiliateDetails.tier || 'base',
                        is_founder: affiliateDetails.is_founder || false,
                        reset_window: false
                      });
                      setShowTierOverride(true);
                    }}
                  >
                    <PencilSimple size={16} className="mr-2" />
                    Override Tier
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tier Override Modal */}
        {showTierOverride && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#121212] border border-white/10 max-w-md w-full">
              <div className="p-6 border-b border-white/10 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Override Tier</h3>
                <button 
                  onClick={() => setShowTierOverride(false)}
                  className="text-zinc-400 hover:text-white"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <Label className="text-white mb-2 block">Select Tier</Label>
                  <select
                    value={tierOverrideData.tier}
                    onChange={(e) => setTierOverrideData({ ...tierOverrideData, tier: e.target.value })}
                    className="w-full bg-[#0a0a0a] border border-white/20 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                  >
                    <option value="base">Base ($0.50)</option>
                    <option value="silver">Silver ($0.60)</option>
                    <option value="gold">Gold ($0.70)</option>
                    <option value="platinum">Platinum ($0.85)</option>
                    <option value="founder">Founder ($1.00)</option>
                  </select>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_founder"
                    checked={tierOverrideData.is_founder}
                    onChange={(e) => setTierOverrideData({ 
                      ...tierOverrideData, 
                      is_founder: e.target.checked,
                      tier: e.target.checked ? 'founder' : tierOverrideData.tier
                    })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="is_founder" className="text-white">Grant Founder Status (Lifetime $1.00)</Label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="reset_window"
                    checked={tierOverrideData.reset_window}
                    onChange={(e) => setTierOverrideData({ ...tierOverrideData, reset_window: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="reset_window" className="text-white">Reset 2-Year Qualification Window</Label>
                </div>

                <p className="text-zinc-500 text-sm">
                  Warning: This will manually override the affiliate's tier. Use with caution.
                </p>
              </div>

              <div className="p-6 border-t border-white/10 flex justify-end gap-3">
                <Button
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5"
                  onClick={() => setShowTierOverride(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                  onClick={handleTierOverride}
                  disabled={actionLoading}
                >
                  {actionLoading ? <Spinner size={16} className="animate-spin" /> : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
